import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMarketingAddon } from '../contexts/MarketingAddonContext';
import { api } from '../lib/api';
import SchoolInsightsPanel from '../components/SchoolInsightsPanel';
import { useToast } from '../hooks/useToast';
import { GlassCard } from '../components/GlassCard';
import ManagerGrid from '../components/ManagerGrid';
import TodayEngine from '../components/TodayEngine';
import AddViaAIDialog from '../components/AddViaAIDialog';
import BakemillCard from '../components/connected/BakemillCard';
import { useConnectedBusinessesEnabled, cbApi, normalizeSummary } from '../lib/connectedBusinesses';
import { Bot, Sparkles, ChevronRight, Plus, Palette, Lock, Check, X } from 'lucide-react';
import { motion } from 'framer-motion';

function formatINR(n) {
  if (!n || n === 0) return '₹0';
  try { return '₹' + Math.round(n).toLocaleString('en-IN'); } catch { return '₹' + String(Math.round(n)); }
}

export default function Home() {
  const { user, tenant, token, planState } = useAuth();
  const { activated: marketingActive, refresh: refreshAddon } = useMarketingAddon();
  const navigate = useNavigate();
  const [businessType, setBusinessType] = useState(null);
  useEffect(() => {
    if (!token) return;
    api.tenantMe(token).then(t => setBusinessType(t?.business_type || null)).catch(() => {});
  }, [token]);
  const isSchoolTenant = businessType === 'school' || businessType === 'coaching';
  const toast = useToast();
  const firstName = user?.name?.split(' ')[0] || 'there';
  const [health, setHealth] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [activationOpen, setActivationOpen] = useState(false);
  const cbEnabled = useConnectedBusinessesEnabled();

  useEffect(() => {
    if (!token) return;
    api.businessHealth(token).then(setHealth).catch(() => {});
  }, [token]);

  // Bakemill tenant only: KPI override from the ERP summary (shares the card's in-flight request).
  const [bmErp, setBmErp] = useState(null);
  const [bmRaw, setBmRaw] = useState(null);
  useEffect(() => {
    if (!token || cbEnabled !== true) return undefined;
    let alive = true;
    cbApi.summary(token, false)
      .then(r => { if (alive) { setBmErp(normalizeSummary(r?.data).erp); setBmRaw(r?.data ?? null); } })
      .catch(() => {}); // silent — fall back to internal values; card owns the error UX
    return () => { alive = false; };
  }, [token, cbEnabled]);
  const bm = cbEnabled === true && bmErp ? bmErp : null;
  const kpiToday = bm?.todaySales ?? health?.kpis?.today_revenue ?? 0;
  const kpiMtd = bm?.monthlyRevenue ?? health?.kpis?.mtd_revenue ?? 0;
  const kpiPending = bm?.pendingPOs ?? health?.recommended_action_request_ids?.length ?? 0;

  const loadBriefing = useCallback(async () => {
    if (!token) return;
    setBriefingLoading(true);
    try {
      const r = await api.aiMorningBriefing(token);
      setBriefing(r?.summary ?? null);
    } catch {
      setBriefing(null);
    } finally {
      setBriefingLoading(false);
    }
  }, [token]);

  useEffect(() => { loadBriefing(); }, [loadBriefing]);

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-6" data-testid="home-dashboard">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex items-center gap-4"
      >
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-brand-purple/25 blur-xl scale-125" />
          <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-brand-purple/30 to-brand-teal/20 border border-white/10 flex items-center justify-center">
            <Bot size={28} className="text-white/80" />
          </div>
        </div>
        <div className="flex-1">
          <h1 className="text-2xl lg:text-3xl font-extrabold text-white tracking-tight">
            Hello, <span className="text-brand-teal">{firstName}.</span>
          </h1>
          <p className="text-white/50 text-sm mt-1">Ask me anything about your business</p>
        </div>
        <button
          data-testid="add-via-ai-open"
          onClick={() => setAiOpen(true)}
          className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-cyan-400 to-teal-500 text-[#0B1220] text-sm font-extrabold shadow-lg shadow-teal-500/20 hover:brightness-110 hover:shadow-teal-500/30 transition-all flex-shrink-0"
        >
          <Plus size={16} strokeWidth={3} />
          Add via AI
        </button>
      </motion.div>

      {/* Trial Banner */}
      {planState?.trial_active && planState.trial_days_left >= 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="home-trial-banner"
          className="glass-sheen relative rounded-3xl overflow-hidden shadow-accent cursor-pointer hover:scale-[0.99] transition-transform"
          style={{ background: 'linear-gradient(135deg, #7C5CFF, #5B3FE5)' }}
        >
          <div className="relative z-[2] flex items-center gap-4 px-5 py-4">
            <div className="w-11 h-11 rounded-full bg-white/20 border border-white/25 flex items-center justify-center flex-shrink-0">
              <Sparkles size={20} className="text-white" />
            </div>
            <div className="flex-1">
              <p className="text-white font-bold text-base">
                {planState.trial_days_left} {planState.trial_days_left === 1 ? 'day' : 'days'} left in your Premium trial
              </p>
              <p className="text-white/75 text-xs mt-0.5">Upgrade any time to keep all AI features</p>
            </div>
            <ChevronRight size={20} className="text-white" />
          </div>
        </motion.div>
      )}

      {/* Today Engine — AI CEO action list */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
      >
        <TodayEngine />
      </motion.div>

      {/* Morning Briefing */}
      {(briefing || briefingLoading) && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <GlassCard
            dark
            className="p-5 cursor-pointer hover:border-white/20 transition-colors"
            onClick={loadBriefing}
            testId="home-briefing-banner"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-brand-purple/15 border border-brand-purple/25 flex items-center justify-center flex-shrink-0">
                <Bot size={18} className="text-brand-purple-deep" style={{ color: '#5B3FE5' }} />
              </div>
              <div className="flex-1">
                <p className="text-brand-purple text-[10px] font-bold tracking-widest uppercase" style={{ color: '#7C4DFF' }}>Morning Briefing</p>
                <p className="text-white/90 text-sm mt-1 leading-relaxed line-clamp-3">
                  {briefingLoading && !briefing ? 'Compiling insights from your 9 AI managers...' : briefing}
                </p>
              </div>
            </div>
          </GlassCard>
        </motion.div>
      )}

      {/* KPI Cards — school tenants get school insights, others get generic */}
      {isSchoolTenant ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <SchoolInsightsPanel />
        </motion.div>
      ) : health && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <GlassCard dark className="p-5" testId="today-card">
            <p className="text-white font-semibold text-sm mb-3 line-clamp-2">{health.one_liner || 'Your daily briefing is ready.'}</p>
            <div className="flex gap-3 flex-wrap">
              <MiniKpi label="Today" value={formatINR(kpiToday)} />
              <MiniKpi label="Overdue" value={formatINR(health.kpis?.outstanding_receivables ?? 0)} />
              <MiniKpi label="MTD Rev" value={formatINR(kpiMtd)} />
              <MiniKpi label="Pending" value={String(kpiPending)} />
            </div>
          </GlassCard>
        </motion.div>
      )}

      {/* Bakemill tenant only — Business Health detail cards (same shared summary fetch) */}
      {cbEnabled === true && bmRaw && <BakemillHealth data={bmRaw} />}

      {/* 9-Manager Grid */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <h2 className="text-white font-bold text-lg mb-3">AI Manager Hub</h2>
        <ManagerGrid />
      </motion.div>

      {/* Marketing Addon Teaser (only when NOT activated) */}
      {marketingActive === false && (
        <GlassCard dark className="p-5" testId="marketing-addon-teaser">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-pink-500/15 border border-pink-500/25 flex items-center justify-center flex-shrink-0">
              <Palette size={22} className="text-pink-400" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="px-2 py-0.5 rounded-full bg-pink-500/15 text-pink-400 text-[10px] font-bold border border-pink-500/25 mb-2 inline-block">Paid Add-on</span>
              <h3 className="text-white font-extrabold text-base">AI Design & Marketing Suite</h3>
              <p className="text-white/50 text-sm mt-1 leading-relaxed">Generate on-brand ads with your logo. Schedule to Instagram & Facebook.</p>
              <button
                data-testid="marketing-activate-cta"
                onClick={() => setActivationOpen(true)}
                className="mt-3 px-5 py-2.5 rounded-xl bg-gradient-to-r from-pink-500 to-pink-600 text-white font-bold text-sm hover:brightness-110 transition-all"
              >
                Activate AI Marketing Suite
              </button>
            </div>
          </div>
        </GlassCard>
      )}

      {/* Connected Businesses — Bakemill ERP (renders nothing until enabled === true) */}
      {cbEnabled === true && (
        <section data-testid="connected-businesses-section">
          <h2 className="text-white font-bold text-lg mb-3">Connected Businesses</h2>
          <BakemillCard />
        </section>
      )}

      {/* Tenant info */}
      <p className="text-white/30 text-xs" data-testid="tenant-chip">
        Tenant · {tenant?.name ?? 'Workspace'}
      </p>

      {/* Add via AI Dialog */}
      <AddViaAIDialog open={aiOpen} onClose={() => setAiOpen(false)} />

      {/* Activation Modal */}
      {activationOpen && (
        <ActivationModal
          token={token}
          onClose={() => setActivationOpen(false)}
          onSuccess={() => { setActivationOpen(false); refreshAddon(); navigate('/brand-assets'); }}
        />
      )}
    </div>
  );
}

/* Bakemill "Business Health" — reads new Phase 25.I.2 keys, falls back to existing ones.
   Any null / 0 / empty card is hidden; whole section hidden if all are. */
const bmNum = (v) => {
  const n = v != null && typeof v === 'object' && !Array.isArray(v) ? (v.total ?? v.amount ?? v.count) : v;
  return n == null || n === '' || !Number.isFinite(Number(n)) ? null : Number(n);
};
const bmCount = (v) => (Array.isArray(v) ? v.length : Array.isArray(v?.items) && v.count == null ? v.items.length : bmNum(v));

function BakemillHealth({ data }) {
  const erp = normalizeSummary(data).erp;
  const raw = data.raw_material_alerts ?? data.manufacturing?.raw_material_stock_alerts;
  const products = (Array.isArray(data.top_products) ? data.top_products : data.top_selling_products || [])
    .filter(p => p && typeof p === 'object').slice(0, 3)
    .map(p => ({ name: p.name || '—', qty: bmNum(p.qty_sold ?? p.quantity), revenue: bmNum(p.revenue) }));
  const tiles = [
    ['Expenses', bmNum(data.expenses), true],
    ['Gross Profit', bmNum(data.gross_profit), true],
    ['GST Collected', bmNum(data.gst_collected), true],
    ['7-day Forecast', bmNum(data.forecast_7_day ?? data.forecast_next_7d_rupees), true],
    ['Low Stock Items', bmNum(data.low_stock_count) ?? erp.lowStockCount, false],
    ['Raw Material Alerts', bmCount(raw), false],
  ].filter(([, v]) => v != null && v !== 0);
  if (tiles.length === 0 && products.length === 0) return null;
  return (
    <GlassCard dark className="p-5" testId="bakemill-business-health">
      <h3 className="text-white font-bold text-sm mb-3">Business Health</h3>
      {tiles.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {tiles.map(([label, v, money]) => (
            <div key={label} className="px-3 py-2 rounded-xl bg-white/8 border border-white/10" data-testid={`bm-health-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <p className="text-white/40 text-[9px] font-bold tracking-wider uppercase">{label}</p>
              <p className="text-white font-bold text-sm tabular-nums mt-0.5">{money ? formatINR(v) : v.toLocaleString('en-IN')}</p>
            </div>
          ))}
        </div>
      )}
      {products.length > 0 && (
        <div className="mt-3" data-testid="bm-health-top-products">
          <p className="text-white/40 text-[9px] font-bold tracking-wider uppercase mb-1">Top Selling Products</p>
          <ul className="divide-y divide-white/10 text-xs">
            {products.map((p, i) => (
              <li key={i} className="flex items-center gap-3 py-1.5" data-testid={`bm-health-top-product-${i}`}>
                <span className="flex-1 min-w-0 truncate text-white font-semibold">{p.name}</span>
                <span className="text-white/50 tabular-nums">{p.qty == null ? '—' : `${p.qty.toLocaleString('en-IN')} sold`}</span>
                <span className="text-white font-bold tabular-nums w-20 text-right">{p.revenue == null ? '—' : formatINR(p.revenue)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </GlassCard>
  );
}

function MiniKpi({ label, value }) {
  return (
    <div className="px-3 py-2 rounded-xl bg-white/8 border border-white/10">
      <p className="text-white/40 text-[10px] font-bold tracking-wider uppercase">{label}</p>
      <p className="text-white font-extrabold text-base tabular-nums mt-0.5">{value}</p>
    </div>
  );
}


/* ── Activation Modal ── */
function ActivationModal({ token, onClose, onSuccess }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [bloom, setBloom] = useState(false);

  const activate = async () => {
    setBusy(true);
    try {
      await api.activateMarketingAddon(token);
      setBloom(true);
      toast.show('AI Marketing Suite activated', 'success');
      setTimeout(onSuccess, 900);
    } catch (e) {
      toast.show(e.message || 'Activation failed', 'error');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#0B1220] border border-white/10 rounded-3xl max-w-md w-full mx-4 p-6 shadow-2xl" data-testid="activation-modal">
        {bloom && (
          <div className="absolute inset-0 flex items-center justify-center z-[60] pointer-events-none ai-bloom-overlay rounded-3xl overflow-hidden" data-testid="activation-bloom">
            <div className="ai-bloom-glow" />
            <div className="ai-bloom-circle">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" className="ai-bloom-check">
                <path d="M12 24L21 33L36 15" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" className="ai-bloom-checkpath" />
              </svg>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-white font-extrabold text-lg flex items-center gap-2"><Palette size={18} className="text-pink-400" /> Activate AI Marketing Suite</h3>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/10 text-white/60"><X size={18} /></button>
        </div>
        <div className="text-center mb-5">
          <p className="text-pink-400 font-extrabold text-3xl tabular-nums">₹499<span className="text-white/40 text-sm font-bold">/month</span></p>
        </div>
        <div className="space-y-2 mb-5">
          {['50 AI designs per month', 'Your logo watermarked on every asset', 'Instagram & Facebook scheduling', 'Credit top-ups any time'].map(f => (
            <div key={f} className="flex items-center gap-2 text-white/70 text-sm">
              <Check size={14} className="text-pink-400 flex-shrink-0" /> {f}
            </div>
          ))}
        </div>
        <p className="text-white/30 text-xs italic mb-4">Simulated purchase — no card charged.</p>
        <button data-testid="activation-confirm-btn" onClick={activate} disabled={busy}
          className="w-full h-12 rounded-xl bg-gradient-to-r from-pink-500 to-pink-600 text-white font-bold text-sm hover:brightness-110 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
          {busy ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Sparkles size={16} />}
          {busy ? 'Activating...' : 'Activate'}
        </button>
        <button onClick={onClose} className="w-full mt-2 text-white/40 text-sm hover:text-white/60 transition-colors" data-testid="activation-cancel-btn">
          Cancel
        </button>
      </div>
    </div>
  );
}
